import { useState, useEffect } from 'react';

export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      {/* WhatsApp Fixed Button */}
      <a 
        href="https://wa.me/522291578562" 
        target="_blank" 
        rel="noopener noreferrer"
        className="fixed left-6 top-1/2 -translate-y-1/2 z-50 bg-gradient-to-r from-green-500 to-green-600 text-white p-4 rounded-full shadow-2xl hover:from-green-400 hover:to-green-500 transition-all transform hover:scale-110 animate-pulse cursor-pointer whitespace-nowrap" 
        aria-label="Contactar por WhatsApp"
      >
        <i className="ri-whatsapp-line text-3xl"></i>
      </a>

      {/* Quick Booking Panel */}
      <div className="fixed right-6 top-1/2 -translate-y-1/2 z-50 bg-gradient-to-br from-slate-800/95 to-slate-900/95 backdrop-blur-xl border-2 border-cyan-500/40 rounded-2xl p-6 shadow-2xl max-w-xs">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <i className="ri-whatsapp-line text-3xl text-white"></i>
          </div>
          <h3 className="text-xl font-bold text-white mb-2">Reserva tu Yate</h3>
          <p className="text-gray-300 text-sm mb-4">por WhatsApp</p>
          <a 
            href="https://wa.me/522291578562?text=Hola,%20me%20interesa%20rentar%20un%20yate.%20¿Podrían%20darme%20más%20información?" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-6 rounded-xl font-semibold hover:from-green-400 hover:to-green-500 transition-all transform hover:scale-105 whitespace-nowrap shadow-lg cursor-pointer block text-center"
          >
            <i className="ri-whatsapp-line mr-2"></i>Contactar Ahora
          </a>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="fixed w-full z-50 transition-all duration-300" style={{
        backgroundColor: isScrolled ? 'rgba(15, 23, 42, 0.95)' : 'transparent',
        backdropFilter: isScrolled ? 'blur(10px)' : 'none'
      }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
                <i className="ri-sailboat-line text-white text-2xl"></i>
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  MAR AZUL
                </h1>
                <p className="text-xs text-gray-400">Renta de Yates Los Cabos</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex space-x-8 items-center">
              <button 
                onClick={() => scrollToSection('inicio')}
                className="text-gray-300 hover:text-cyan-400 font-medium transition-colors relative group cursor-pointer whitespace-nowrap"
              >
                Inicio
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-cyan-400 transition-all group-hover:w-full"></span>
              </button>
              <button 
                onClick={() => scrollToSection('productos')}
                className="text-gray-300 hover:text-cyan-400 font-medium transition-colors relative group cursor-pointer whitespace-nowrap"
              >
                Productos
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-cyan-400 transition-all group-hover:w-full"></span>
              </button>
              <button 
                onClick={() => scrollToSection('detalles-producto')}
                className="text-gray-300 hover:text-cyan-400 font-medium transition-colors relative group cursor-pointer whitespace-nowrap"
              >
                Detalles del producto
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-cyan-400 transition-all group-hover:w-full"></span>
              </button>
              <button 
                onClick={() => scrollToSection('sobre-nosotros')}
                className="text-gray-300 hover:text-cyan-400 font-medium transition-colors relative group cursor-pointer whitespace-nowrap"
              >
                Sobre nosotros
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-cyan-400 transition-all group-hover:w-full"></span>
              </button>
              <button 
                onClick={() => scrollToSection('contacto')}
                className="text-gray-300 hover:text-cyan-400 font-medium transition-colors relative group cursor-pointer whitespace-nowrap"
              >
                Contacto
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-cyan-400 transition-all group-hover:w-full"></span>
              </button>
              <a 
                href="https://wa.me/522291578562?text=Hola,%20quiero%20hacer%20una%20reserva%20de%20yate.%20¿Podrían%20ayudarme?" 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-semibold rounded-full transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap shadow-lg relative overflow-hidden bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-400 hover:to-blue-500 px-6 py-3 text-base"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-700"></div>
                <span className="relative z-10 flex items-center justify-center">Reservar Ahora</span>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-white p-2 cursor-pointer whitespace-nowrap"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden bg-slate-800/95 backdrop-blur-xl border border-cyan-500/20 rounded-lg mt-2 p-4 space-y-4">
              <button 
                onClick={() => scrollToSection('inicio')}
                className="block w-full text-left text-gray-300 hover:text-cyan-400 font-medium py-2 cursor-pointer whitespace-nowrap"
              >
                Inicio
              </button>
              <button 
                onClick={() => scrollToSection('productos')}
                className="block w-full text-left text-gray-300 hover:text-cyan-400 font-medium py-2 cursor-pointer whitespace-nowrap"
              >
                Productos
              </button>
              <button 
                onClick={() => scrollToSection('detalles-producto')}
                className="block w-full text-left text-gray-300 hover:text-cyan-400 font-medium py-2 cursor-pointer whitespace-nowrap"
              >
                Detalles del producto
              </button>
              <button 
                onClick={() => scrollToSection('sobre-nosotros')}
                className="block w-full text-left text-gray-300 hover:text-cyan-400 font-medium py-2 cursor-pointer whitespace-nowrap"
              >
                Sobre nosotros
              </button>
              <button 
                onClick={() => scrollToSection('contacto')}
                className="block w-full text-left text-gray-300 hover:text-cyan-400 font-medium py-2 cursor-pointer whitespace-nowrap"
              >
                Contacto
              </button>
            </div>
          )}
        </div>
      </nav>
    </>
  );
}